import { type AccountUser, type SiteSettings, api } from '../api.ts';
import { el } from '../components/ui.ts';

/**
 * Het beheermenu: een hamburgerknop rechtsbovenaan die een paneeltje openklapt.
 *
 * Uitgelogd staat daar een aanmeldformulier, aangemeld de site-instellingen en
 * je wachtwoord. Meer niet — er is één beheerder, dus er valt geen
 * gebruikerslijst te tonen.
 *
 * Het hangt absoluut in `.wrap` en niet vast in de viewporthoek: vast zou op een
 * telefoon over de inhoud liggen en straks over de coverfoto zweven, voor een
 * knop die je twee keer per jaar aanraakt. Meescrollen mag dus.
 *
 * Geen verduisterde overlay eronder. Dit is geen beslissing die de pagina moet
 * blokkeren; je klikt ernaast en het is weg.
 */

const BURGER = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16"/><path
  d="M4 12h16"/><path d="M4 17h16"/></svg>`;

const PANEL_ID = 'beheerpaneel';

/**
 * `read` en niet een meegegeven waarde: de instellingen komen binnen nadat de
 * pagina al staat, en op een trage verbinding valt de eerste verf terug op de
 * standaard. Met een momentopname bleef dit menu daar voorgoed op hangen — het
 * meldde dan "nog geen foto" terwijl er een foto lag, en de eerstvolgende
 * schakelaarklik stuurde die verouderde toestand terug naar de pagina.
 */
export function adminMenu(options: {
  read: () => SiteSettings;
  write: (settings: SiteSettings) => void;
}): HTMLElement {
  const root = el('div', 'admin');

  let user: AccountUser | null = null;
  let open = false;
  let message: { text: string; bad: boolean } | null = null;
  /** Of de verwijderknop op zijn tweede klik staat te wachten. */
  let confirmRemove = false;

  const toggle = el('button', 'admin-toggle', BURGER);
  toggle.type = 'button';
  toggle.setAttribute('aria-controls', PANEL_ID);
  toggle.setAttribute('aria-label', 'Beheer');

  const panel = el('div', 'admin-panel');
  panel.id = PANEL_ID;

  toggle.addEventListener('click', () => setOpen(!open));

  root.append(toggle, panel);

  function say(text: string, bad = false): void {
    message = { text, bad };
    render();
  }

  /** Voert een actie uit en zet de melding, zodat elke knop hetzelfde doet bij een fout. */
  async function attempt(run: () => Promise<string | void>): Promise<void> {
    // Elke andere handeling ontspant de verwijderknop. Zonder dit blijft hij op
    // "Zeker weten?" staan terwijl je intussen iets heel anders deed — een
    // vervangende foto uploaden bijvoorbeeld — en wist de volgende klik die
    // verse foto zonder nog iets te vragen.
    confirmRemove = false;
    try {
      const ok = await run();
      if (typeof ok === 'string') say(ok);
      else render();
    } catch (error) {
      say(error instanceof Error ? error.message : 'Er ging iets mis.', true);
    }
  }

  /*
   * Escape en een klik ernaast sluiten het paneel. De luisteraars hangen aan het
   * document en staan er alleen zolang het openstaat: een menu dat je twee keer
   * per jaar opent hoeft de rest van de tijd niet mee te luisteren met elke klik
   * op de pagina.
   */
  function onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') setOpen(false, { focusToggle: true });
  }

  function onPointer(event: PointerEvent): void {
    if (!root.contains(event.target as Node)) setOpen(false);
  }

  function setOpen(next: boolean, { focusToggle = false } = {}): void {
    if (open === next) return;
    open = next;
    message = null;
    confirmRemove = false;

    if (open) {
      document.addEventListener('keydown', onKey);
      document.addEventListener('pointerdown', onPointer);
    } else {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    }

    render();
    // Alleen bij het aanmeldformulier springt de focus naar het eerste veld:
    // daar wil je meteen kunnen typen. Aangemeld is het eerste invoerveld de
    // bestandskiezer, en die scherp zetten zonder dat iemand erom vroeg is
    // eerder verwarrend dan behulpzaam.
    if (open && !user) panel.querySelector('input')?.focus();
    if (focusToggle) toggle.focus();
  }

  function render(): void {
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('is-open', open);
    panel.hidden = !open;
    if (!open) {
      panel.replaceChildren();
      return;
    }

    panel.replaceChildren(user ? signedIn() : signInForm());
    if (message) {
      const note = el('p', message.bad ? 'admin-msg bad' : 'admin-msg');
      note.textContent = message.text;
      panel.append(note);
    }
  }

  function signInForm(): HTMLElement {
    const form = el('form', 'admin-form');
    const email = field('email', 'E-mailadres', 'username');
    const password = field('password', 'Wachtwoord', 'current-password');

    form.append(email.wrap, password.wrap, submit('Aanmelden'));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void attempt(async () => {
        const result = await api.login(email.input.value, password.input.value);
        user = result.user;
        message = null;
        // De instelling kan intussen elders gewijzigd zijn; dit is het
        // goedkoopste moment om weer gelijk te lopen.
        options.write(await api.settings());
      });
    });

    return form;
  }

  function signedIn(): HTMLElement {
    const wrap = el('div', 'admin-signed-in');

    const who = el('p', 'admin-who');
    who.textContent = user?.email ?? '';
    wrap.append(who, settingsRow(), coverSection(), passwordForm(), signOutButton());
    return wrap;
  }

  function settingsRow(): HTMLElement {
    const section = el('div', 'admin-section');
    section.append(heading('Site-instellingen'));

    const label = el('label', 'admin-switch');
    label.append(document.createTextNode('Achtergrond volgt het weer'));

    const button = el('button', 'switch', '<span class="knob"></span>');
    button.type = 'button';
    button.setAttribute('aria-pressed', String(options.read().weatherBackground));

    const state = el('span', 'admin-state');
    state.textContent = options.read().weatherBackground ? 'aan' : 'uit';

    button.addEventListener('click', () => {
      const vorige = options.read();
      const next = !vorige.weatherBackground;
      // Meteen toepassen en pas daarna bewaren: je ziet het resultaat van je
      // klik zonder op de server te wachten.
      options.write({ ...vorige, weatherBackground: next });

      void attempt(async () => {
        try {
          options.write(await api.saveSettings({ weatherBackground: next }));
        } catch (error) {
          // En terugdraaien als het bewaren mislukt. Zonder dit blijft de pagina
          // de nieuwe stand tonen naast een foutmelding, en denk je dat het half
          // gelukt is — tot een herlaadbeurt alles stilletjes terugzet.
          options.write(vorige);
          throw error;
        }
        return 'Bewaard.';
      });
    });

    label.append(button, state);
    section.append(label);
    return section;
  }

  /**
   * De coverfoto: kiezen, of weghalen.
   *
   * Kiezen uploadt meteen — een aparte "Versturen"-knop naast een bestandskiezer
   * die je toch al bewust aanklikte, voegt niets toe. Verwijderen vraagt om een
   * tweede klik, want dat is wél onherstelbaar en een bevestigingsvenster
   * blokkeert de pagina.
   */
  function coverSection(): HTMLElement {
    const section = el('div', 'admin-section');
    section.append(heading('Coverfoto'));

    const cover = options.read().cover;

    const status = el('p', 'admin-note');
    status.textContent = cover ? `${cover.width} × ${cover.height}` : 'Nog geen foto.';
    section.append(status);

    const pick = el('label', 'btn-ghost admin-file');
    pick.append(document.createTextNode(cover ? 'Vervangen' : 'Kiezen'));

    const input = el('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void attempt(async () => {
        const result = await api.uploadCover(file);
        options.write({ ...options.read(), cover: result.cover });
        return 'Coverfoto bijgewerkt.';
      });
    });

    pick.append(input);
    section.append(pick);

    if (cover) {
      section.append(
        button(confirmRemove ? 'Zeker weten?' : 'Verwijderen', () => {
          if (!confirmRemove) {
            confirmRemove = true;
            render();
            return;
          }
          void attempt(async () => {
            await api.removeCover();
            options.write({ ...options.read(), cover: null });
            return 'Coverfoto verwijderd.';
          });
        }),
      );
    }

    return section;
  }

  function passwordForm(): HTMLElement {
    const section = el('div', 'admin-section');
    section.append(heading('Wachtwoord'));

    const form = el('form', 'admin-form');
    const password = field('password', 'Nieuw wachtwoord', 'new-password');
    form.append(password.wrap, submit('Wijzigen'));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void attempt(async () => {
        await api.setPassword(password.input.value);
        password.input.value = '';
        return 'Wachtwoord gewijzigd.';
      });
    });

    section.append(form);
    return section;
  }

  function signOutButton(): HTMLElement {
    const button = el('button', 'btn-ghost admin-signout');
    button.type = 'button';
    button.textContent = 'Afmelden';
    button.addEventListener('click', () => {
      void attempt(async () => {
        await api.logout();
        user = null;
        return 'Afgemeld.';
      });
    });
    return button;
  }

  // Wie er is aangemeld weten we pas na een vraag aan de server. Mislukt die,
  // dan blijft het menu gewoon het aanmeldformulier tonen — de site werkt ook
  // zonder beheer.
  render();
  void api
    .me()
    .then((result) => {
      user = result.user;
      if (open) render();
    })
    .catch(() => {});

  return root;
}

function button(text: string, onClick: () => void): HTMLButtonElement {
  const node = el('button', 'btn-ghost');
  node.type = 'button';
  node.textContent = text;
  node.addEventListener('click', onClick);
  return node;
}

function heading(text: string): HTMLElement {
  const node = el('p', 'admin-heading');
  node.textContent = text;
  return node;
}

function submit(text: string): HTMLButtonElement {
  const node = el('button', 'btn-ghost');
  node.type = 'submit';
  node.textContent = text;
  return node;
}

/**
 * Een veld met een echt label erboven in plaats van alleen een placeholder: een
 * placeholder verdwijnt zodra je typt, en dan staat er nergens meer wat je aan
 * het invullen bent.
 *
 * `autocomplete` is hier geen detail. Een veld voor een níeuw wachtwoord dat
 * `current-password` heet, laat de wachtwoordbeheerder je bestaande wachtwoord
 * invullen — waarna één klik het "wijzigt" naar wat het al was.
 */
function field(
  type: 'email' | 'password',
  label: string,
  autocomplete: AutoFill,
): { wrap: HTMLElement; input: HTMLInputElement } {
  const wrap = el('label', 'admin-field');
  const text = el('span');
  text.textContent = label;

  const input = el('input');
  input.type = type;
  input.autocomplete = autocomplete;
  input.required = true;

  wrap.append(text, input);
  return { wrap, input };
}
